import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VerifiedIcon from '@mui/icons-material/Verified';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  CAREGIVER_STATUS_COPY,
  CredentialDocStatus,
  CredentialDocType,
  CredentialDocument,
  CREDENTIAL_DOC_ALLOWED_MIME,
  CREDENTIAL_DOC_MAX_BYTES,
  CREDENTIAL_DOC_TYPE_OPTIONS,
  credentialDocumentService,
} from '../services/credentialDocumentService';

// FEAT-046 — Caregiver Credential Document Uploader (MVP-18.20, MVP-18.21).
//
// File picker accepting PDF / JPG / PNG / HEIC up to 25 MB with a title,
// document-type selector, and optional notes. After upload the surface
// also lists the caregiver's existing documents with warm review status
// copy ("Reviewing your document" / "Document accepted" / "Needs another
// look"). Accepted documents are the source of the credential badges that
// FEAT-039 surfaces on the public profile.
//
// Tone follows the §1.2 elderly-friendly guideline — encouraging language,
// no compliance / failure framing. Errors are surfaced as gentle prompts.

const ACCEPT_ATTRIBUTE = CREDENTIAL_DOC_ALLOWED_MIME.join(',');

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusChip({ status }: { status: CredentialDocStatus }) {
  const copy = CAREGIVER_STATUS_COPY[status];
  switch (status) {
    case 'ACCEPTED':
      return (
        <Chip
          icon={<VerifiedIcon aria-hidden="true" />}
          color="success"
          label={copy.label}
          size="small"
          data-testid={`credential-doc-status-${status}`}
        />
      );
    case 'PENDING_REVIEW':
      return (
        <Chip
          icon={<HourglassTopIcon aria-hidden="true" />}
          color="info"
          label={copy.label}
          size="small"
          data-testid={`credential-doc-status-${status}`}
        />
      );
    case 'REJECTED':
      return (
        <Chip
          icon={<ErrorOutlineIcon aria-hidden="true" />}
          color="warning"
          label={copy.label}
          size="small"
          data-testid={`credential-doc-status-${status}`}
        />
      );
    default: {
      // exhaustive switch
      const _exhaustive: never = status;
      return <Chip label={String(_exhaustive)} size="small" />;
    }
  }
}

export interface CredentialDocumentUploaderProps {
  /** Optional callback invoked after a successful upload. */
  onUploaded?: (doc: CredentialDocument) => void;
  /** Optional override of the initial document list — used for tests. */
  initialDocuments?: CredentialDocument[];
}

export default function CredentialDocumentUploader({
  onUploaded,
  initialDocuments,
}: CredentialDocumentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<CredentialDocType>('STATE_CERT');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [documents, setDocuments] = useState<CredentialDocument[]>(
    initialDocuments ?? [],
  );
  const [loadingDocs, setLoadingDocs] = useState(!initialDocuments);
  const [listError, setListError] = useState<string | null>(null);

  const loadDocuments = async () => {
    setLoadingDocs(true);
    setListError(null);
    try {
      const result = await credentialDocumentService.listMyDocuments();
      setDocuments(result.documents);
    } catch (err) {
      setListError(
        err instanceof Error ? err.message : 'We could not load your documents.',
      );
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    if (!initialDocuments) {
      void loadDocuments();
    }
    // We intentionally only load on mount; refresh happens on upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const helperText = useMemo(
    () =>
      file
        ? `${file.name} • ${formatSize(file.size)}`
        : 'PDF, JPG, PNG, or HEIC — up to 25 MB',
    [file],
  );

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setFile(null);
      return;
    }
    const validation = credentialDocumentService.validateFile(picked);
    if (validation) {
      setError(validation);
      setFile(null);
      return;
    }
    setFile(picked);
    if (!title) {
      setTitle(picked.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a document to upload.');
      return;
    }
    if (!title.trim()) {
      setError('A short title helps our team know what they are looking at.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await credentialDocumentService.uploadDocument({
        file,
        title: title.trim(),
        docType,
        notes: notes.trim() || undefined,
      });
      setSuccess(
        'Thank you — we received your document and will take a careful look.',
      );
      setFile(null);
      setTitle('');
      setNotes('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setDocuments((prev) => [result.document, ...prev]);
      onUploaded?.(result.document);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We could not upload that document right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="credential-document-uploader">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <CloudUploadIcon color="primary" aria-hidden="true" />
          <Typography variant="h6" component="h2">
            Share a credential document
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload a certificate, transcript, or training proof. Our team takes
          a careful look and adds it to your profile.
        </Typography>

        {error && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="credential-uploader-error">
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} data-testid="credential-uploader-success">
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              data-testid="credential-uploader-file-button"
            >
              {file ? 'Choose a different document' : 'Choose document'}
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={ACCEPT_ATTRIBUTE}
                onChange={handleSelectFile}
                data-testid="credential-uploader-file-input"
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {helperText}{' '}
              {file
                ? ''
                : `(${(CREDENTIAL_DOC_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB limit)`}
            </Typography>

            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. California HCSB Certificate"
              inputProps={{
                maxLength: 200,
                'data-testid': 'credential-uploader-title',
              }}
              required
            />

            <FormControl>
              <InputLabel id="credential-doc-type-label">Document type</InputLabel>
              <Select
                labelId="credential-doc-type-label"
                label="Document type"
                value={docType}
                onChange={(e) => setDocType(e.target.value as CredentialDocType)}
                inputProps={{ 'data-testid': 'credential-uploader-doctype' }}
              >
                {CREDENTIAL_DOC_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              placeholder="Anything our team should know about this document?"
              inputProps={{
                maxLength: 1000,
                'data-testid': 'credential-uploader-notes',
              }}
            />

            <Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting || !file}
                data-testid="credential-uploader-submit"
              >
                {submitting ? 'Uploading…' : 'Send for review'}
              </Button>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" component="h3" gutterBottom>
          Your documents
        </Typography>
        {loadingDocs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} aria-label="Loading your documents" />
          </Box>
        ) : listError ? (
          <Alert severity="info" data-testid="credential-uploader-list-error">
            {listError}
          </Alert>
        ) : documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents yet. When you share one, you'll see its status here.
          </Typography>
        ) : (
          <List dense data-testid="credential-uploader-list">
            {documents.map((doc) => {
              const copy = CAREGIVER_STATUS_COPY[doc.status];
              const reason = doc.reviewDecision?.reason;
              return (
                <ListItem
                  key={doc.id}
                  alignItems="flex-start"
                  data-testid={`credential-doc-row-${doc.id}`}
                  sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="subtitle2">{doc.title}</Typography>
                    <StatusChip status={doc.status} />
                  </Stack>
                  <ListItemText
                    primary={copy.helper}
                    secondary={
                      <>
                        {doc.docType.replace(/_/g, ' ')} •{' '}
                        {formatSize(doc.sizeBytes)} •{' '}
                        Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                        {doc.status === 'REJECTED' && reason ? (
                          <>
                            <br />
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              Note from our team: {reason}
                            </Typography>
                          </>
                        ) : null}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
